// ── BCHNostr — Podcast seeding (operator only) ────────────────────
//
// A one-time tool for the operator to populate the BCH Podcasts directory
// with real shows. Paste a list of podcast links (Apple, Spotify, RSS, …),
// one per line; each is resolved to its RSS feed and published under its own
// freshly generated Nostr identity (see lib/podcastSeed.ts).
//
// The generated nsecs are shown once and can be downloaded — SAVE THEM if
// you ever want to update or remove a seeded show, since each show is owned
// by its own key, not yours.

import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Sparkles, Loader2, CheckCircle, AlertTriangle,
  Download, Copy, Check, ShieldAlert, Rocket, KeyRound,
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { OPERATOR_PUBKEY } from '../lib/platform';
import { podcastPath } from '../lib/podcast';
import { platformLabel } from '../lib/podcastResolver';
import {
  parseSeedInput, seedPodcasts,
  type SeedResult, type SeedProgress,
} from '../lib/podcastSeed';

export default function PodcastSeedPage() {
  const { user } = useAppContext();
  const isOperator = !!user && user.pubkey === OPERATOR_PUBKEY;

  const [text, setText] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<SeedProgress | null>(null);
  const [results, setResults] = useState<SeedResult[]>([]);
  const [copied, setCopied] = useState(false);

  const inputs = useMemo(() => parseSeedInput(text), [text]);
  const updateCount = useMemo(() => inputs.filter(i => i.nsec).length, [inputs]);

  const handleRun = async () => {
    if (running || inputs.length === 0) return;
    setRunning(true);
    setResults([]);
    setProgress(null);
    const collected: SeedResult[] = [];
    await seedPodcasts(
      inputs,
      (p, result) => {
        setProgress(p);
        if (result) {
          collected.push(result);
          setResults([...collected]);
        }
      },
      { passphrase: passphrase.trim() || undefined, operatorPubkey: user?.pubkey },
    );
    setProgress(null);
    setRunning(false);
  };

  const okResults = results.filter(r => r.status === 'ok');
  const failedResults = results.filter(r => r.status === 'failed');
  const newlyCreated = okResults.filter(r => !r.updated);
  // Random one-off keys are the ONLY copy and must be saved. Deterministic
  // (passphrase-derived) keys can always be re-derived, so they don't.
  const atRiskKeys = okResults.filter(r => !r.deterministic && !r.updated);

  // A re-pasteable backup of every seeded identity. The line format —
  // "url, nsec" — is exactly what parseSeedInput reads back, so pasting this
  // file straight into the box re-runs as an UPDATE of the same shows. Only
  // newly-generated identities are the sole copy; still, we export them all.
  const keysBlob = useMemo(() => {
    if (okResults.length === 0) return '';
    return okResults
      .map(r =>
        `# ${r.name}${r.updated ? ' (updated)' : ''}\n` +
        `# npub: ${r.npub}\n` +
        `${r.feedUrl}, ${r.nsec}\n`,
      )
      .join('\n');
  }, [okResults]);

  const handleDownloadKeys = () => {
    const blob = new Blob([keysBlob], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bchnostr-seeded-podcasts-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopyKeys = async () => {
    try {
      await navigator.clipboard.writeText(keysBlob);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  // ── Access control ──
  if (!isOperator) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3 p-6 text-center">
        <ShieldAlert className="w-14 h-14 text-muted-foreground/25" />
        <h2 className="text-lg font-bold">Operator only</h2>
        <p className="text-sm text-muted-foreground max-w-sm leading-relaxed">
          This seeding tool is restricted to the BCHNostr operator account.
        </p>
        <Link
          to="/podcasts"
          className="mt-1 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90"
        >
          Back to BCH Podcasts
        </Link>
      </div>
    );
  }

  return (
    <div className="pb-8">
      {/* ── Header ── */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3 flex items-center gap-2">
        <Link to="/podcasts" className="p-1.5 -ml-1.5 rounded-lg hover:bg-muted text-muted-foreground">
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <h1 className="text-base font-bold flex items-center gap-2 flex-1">
          <Rocket className="w-4 h-4 text-primary" /> Seed Podcasts
        </h1>
      </div>

      <div className="p-4 space-y-4 max-w-2xl mx-auto">
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 space-y-1.5">
          <p className="text-[12px] font-semibold flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-primary" /> Populate the directory
          </p>
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Paste podcast links — one per line. Apple Podcasts, Spotify, Amazon Music, a
            show’s website, or a raw RSS feed all work. Each show is published under its own
            Nostr identity, so it appears as an independent publisher.
          </p>
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            <strong className="text-foreground">Set a passphrase</strong> and each show’s key
            is derived from it plus the show’s feed URL — so to update a show later you just
            re-enter the same passphrase and paste its URL again. Nothing to save, no keys to
            lose. Use the <em>same</em> passphrase every time.
          </p>
        </div>

        {/* ── Passphrase (the master secret for deterministic keys) ── */}
        <div>
          <label className="text-[11px] font-medium text-muted-foreground">
            Seed passphrase <span className="text-muted-foreground/60">(recommended)</span>
          </label>
          <input
            type="password"
            value={passphrase}
            onChange={e => setPassphrase(e.target.value)}
            disabled={running}
            autoComplete="off"
            spellCheck={false}
            placeholder="A secret only you know — reuse it to update shows"
            className="w-full bg-background rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
          />
          <p className="text-[10px] text-muted-foreground mt-1 leading-relaxed">
            {passphrase.trim()
              ? 'Deterministic mode: the same passphrase + feed URL always produces the same show. Remember it — it can’t be recovered, and a different passphrase makes different shows.'
              : 'Leave blank to generate random one-off keys instead (you’d have to save each nsec to update it later).'}
          </p>
        </div>

        {/* ── Input ── */}
        <div>
          <label className="text-[11px] font-medium text-muted-foreground">
            Podcast URLs <span className="text-primary">*</span>
          </label>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            disabled={running}
            rows={8}
            spellCheck={false}
            placeholder={'https://podcasts.apple.com/us/podcast/gp-spaces/id1797937833\nhttps://feeds.example.com/another-show.xml\n# advanced: pin a specific key by adding an nsec on the line\nhttps://feeds.example.com/legacy-show.xml, nsec1...'}
            className="w-full bg-background rounded-lg px-3 py-2 text-xs font-mono mt-1 resize-y focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
          />
          <p className="text-[10px] text-muted-foreground mt-1">
            {inputs.length} link{inputs.length === 1 ? '' : 's'} detected
            {updateCount > 0 && ` · ${updateCount} with a pinned key`}
            {passphrase.trim() && inputs.length - updateCount > 0
              ? ` · ${inputs.length - updateCount} deterministic` : ''}.
          </p>
        </div>

        <button
          onClick={handleRun}
          disabled={running || inputs.length === 0}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 disabled:opacity-50"
        >
          {running
            ? <><Loader2 className="w-4 h-4 animate-spin" /> Working…</>
            : <><Rocket className="w-4 h-4" /> {updateCount === inputs.length && updateCount > 0 ? 'Update' : 'Seed'} {inputs.length || ''} podcast{inputs.length === 1 ? '' : 's'}</>}
        </button>

        {/* ── Live progress ── */}
        {running && progress && (
          <div className="rounded-lg border border-border bg-muted/30 p-3">
            <div className="flex items-center gap-2 text-[12px]">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-primary shrink-0" />
              <span className="font-medium">
                {progress.index + 1} / {progress.total}
              </span>
              <span className="text-muted-foreground truncate">
                {progress.phase === 'resolving' ? 'Finding feed…' : progress.phase === 'publishing' ? 'Publishing…' : 'Done'}
              </span>
            </div>
            <p className="text-[10px] text-muted-foreground font-mono truncate mt-1">{progress.input}</p>
            <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${((progress.index) / progress.total) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* ── Results ── */}
        {results.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-[12px] font-semibold">
              {newlyCreated.length > 0 && (
                <span className="text-primary">{newlyCreated.length} added</span>
              )}
              {okResults.length - newlyCreated.length > 0 && (
                <span className="text-primary">
                  {newlyCreated.length > 0 ? '· ' : ''}{okResults.length - newlyCreated.length} updated
                </span>
              )}
              {failedResults.length > 0 && (
                <span className="text-red-500">· {failedResults.length} failed</span>
              )}
            </div>

            {/* Keys backup. Deterministic (passphrase-derived) keys are always
                recoverable, so the panel is only URGENT when random one-off
                keys were minted — those are the sole copy. */}
            {okResults.length > 0 && (
              <div className={`rounded-xl border-2 p-3 space-y-2 ${
                atRiskKeys.length > 0
                  ? 'border-amber-500/40 bg-amber-500/5'
                  : 'border-border bg-muted/30'
              }`}>
                {atRiskKeys.length > 0 ? (
                  <>
                    <p className="text-[12px] font-bold flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
                      <AlertTriangle className="w-3.5 h-3.5" /> Save these keys now
                    </p>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      {atRiskKeys.length} show{atRiskKeys.length === 1 ? '' : 's'} used a random
                      one-off key — this is the only copy. Without it you can never update or
                      remove {atRiskKeys.length === 1 ? 'it' : 'them'}. Set a passphrase next
                      time to avoid this. The download uses a{' '}
                      <span className="font-mono">url, nsec</span> format you can paste back in.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-[12px] font-bold flex items-center gap-1.5">
                      <CheckCircle className="w-3.5 h-3.5 text-primary" /> Keys are safe
                    </p>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      These shows use passphrase-derived keys — re-enter the same passphrase
                      and paste the URLs to update them anytime. A key backup is optional.
                    </p>
                  </>
                )}
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleDownloadKeys}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-[11px] font-semibold hover:bg-primary/90"
                  >
                    <Download className="w-3 h-3" /> Download keys
                  </button>
                  <button
                    onClick={handleCopyKeys}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-muted text-[11px] font-semibold hover:bg-muted/70"
                  >
                    {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {copied ? 'Copied' : 'Copy keys'}
                  </button>
                </div>
              </div>
            )}

            {/* Per-show result rows */}
            <div className="space-y-2">
              {results.map((r, i) => (
                <SeedResultRow key={`${r.input}-${i}`} result={r} />
              ))}
            </div>

            <Link
              to="/podcasts"
              className="flex items-center justify-center gap-1.5 w-full px-4 py-2.5 rounded-lg bg-muted text-sm font-semibold hover:bg-muted/70"
            >
              View BCH Podcasts
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Result row ────────────────────────────────────────────────────

function SeedResultRow({ result }: { result: SeedResult }) {
  const ok = result.status === 'ok';
  return (
    <div className={`flex items-start gap-2.5 rounded-lg border p-2.5 ${
      ok ? 'border-primary/25 bg-primary/[0.03]' : 'border-red-500/25 bg-red-500/[0.03]'
    }`}>
      {ok
        ? <CheckCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" />
        : <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />}
      <div className="min-w-0 flex-1">
        {ok ? (
          <>
            <div className="flex items-center gap-1.5">
              <p className="text-[13px] font-semibold truncate">{result.name}</p>
              <span className={`shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${
                result.updated
                  ? 'bg-sky-500/15 text-sky-600 dark:text-sky-400'
                  : 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
              }`}>
                {result.updated ? 'Updated' : 'Published'}
              </span>
              {result.deterministic && (
                <span
                  className="shrink-0 flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-primary/10 text-primary"
                  title="Key derived from your passphrase — re-runnable anytime"
                >
                  <KeyRound className="w-2 h-2" /> Deterministic
                </span>
              )}
              {result.platform && (
                <span className="shrink-0 text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">
                  {platformLabel(result.platform)}
                </span>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              {result.episodes} episode{result.episodes === 1 ? '' : 's'} · {result.acceptedRelays} relay{result.acceptedRelays === 1 ? '' : 's'}
            </p>
            <p className="text-[10px] font-mono text-muted-foreground/80 truncate mt-0.5">{result.feedUrl}</p>
            <Link
              to={podcastPath(result.pubkey)}
              className="inline-block text-[10px] font-semibold text-primary hover:underline mt-0.5"
            >
              View show page →
            </Link>
          </>
        ) : (
          <>
            <p className="text-[12px] font-mono text-muted-foreground truncate">{result.input}</p>
            <p className="text-[11px] text-red-500 mt-0.5">{result.error}</p>
          </>
        )}
      </div>
    </div>
  );
}
